// Account Mobile 2.1 — full redesign
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { soundTick } from "../../utils/sound";
import { hapticTap } from "../../utils/haptics";
import { f$, fN, LifeCardPopup, API } from "./_shared";
import { fastGetAsync } from "../../lib/fastFetch";
import { Skel } from "./Skel";

// ─── Palette ────────────────────────────────────────────────────────────────
const C = {
  blue:   "#60a5fa", sky:    "#38bdf8", green:  "#4ade80", red:    "#f87171",
  purple: "#a78bfa", cyan:   "#22d3ee", gold:   "#fbbf24", orange: "#ff9f0a",
  amber:  "#f59e0b", pink:   "#f472b6",
};

// ─── Chart layer config ─────────────────────────────────────────────────────
const LAYERS = [
  { id:"portfolio",   label:"Portfolio",     icon:"◉",  color: C.blue   },
  { id:"ma7",         label:"MA 7",          icon:"~",  color: C.gold   },
  { id:"forecast",    label:"Ω Forecast",    icon:"◈",  color: C.purple },
  { id:"buySignal",   label:"Buy Signal",    icon:"▲",  color: C.green  },
  { id:"sellSignal",  label:"Sell Signal",   icon:"▼",  color: C.red    },
  { id:"aiPick",      label:"AI Pick",       icon:"★",  color: C.cyan   },
  { id:"omegaUp",     label:"Omega ↑",       icon:"Ω",  color:"#6ee7b7" },
  { id:"omegaDown",   label:"Omega ↓",       icon:"Ω",  color:"#fca5a5" },
  { id:"regimeShift", label:"Regime Shift",  icon:"|",  color: C.amber  },
  { id:"intelAlert",  label:"Intel Alert",   icon:"⚡", color: C.orange },
  { id:"signalPeak",  label:"Signal Peak",   icon:"◆",  color: C.pink   },
  { id:"deposit",     label:"Deposit",       icon:"↓",  color: C.sky    },
];
const DEFAULT_LAYERS = { portfolio:true, ma7:true, forecast:true, buySignal:true, sellSignal:true, aiPick:false, omegaUp:false, omegaDown:false, regimeShift:false, intelAlert:false, signalPeak:false, deposit:false };

// ─── Helpers ─────────────────────────────────────────────────────────────────
function pct(a, b) { return b && b > 0 ? ((a - b) / b * 100) : 0; }

function sign(v) { return v >= 0 ? "+" : ""; }

// ─── Bezier path helper (Catmull-Rom tension) ────────────────────────────────
function smoothPath(pts, tension = 0.18) {
  if (!pts || pts.length < 2) return "";
  let d = `M ${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`;
  for (let i = 1; i < pts.length; i++) {
    const p0 = pts[Math.max(0, i - 2)];
    const p1 = pts[i - 1];
    const p2 = pts[i];
    const p3 = pts[Math.min(pts.length - 1, i + 1)];
    // Clamp X handles so they stay inside segment — prevents X-axis loops
    let cp1x = Math.min(p2.x, p1.x + (p2.x - p0.x) * tension);
    let cp2x = Math.max(p1.x, p2.x - (p3.x - p1.x) * tension);
    if (cp1x > cp2x) { const mid = (cp1x + cp2x) / 2; cp1x = cp2x = mid; }
    // Clamp Y handles between adjacent points — prevents vertical overshoots
    const yLo = Math.min(p1.y, p2.y), yHi = Math.max(p1.y, p2.y);
    const cp1y = Math.min(yHi, Math.max(yLo, p1.y + (p2.y - p0.y) * tension));
    const cp2y = Math.min(yHi, Math.max(yLo, p2.y - (p3.y - p1.y) * tension));
    d += ` C ${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
  }
  return d;
}

// ─── Omega prediction generator ──────────────────────────────────────────────
function generateOmegaPrediction(vals, regime, N = 10) {
  if (!vals || vals.length < 2 || N < 1) return { pts:[], upper:[], lower:[] };
  const last = vals[vals.length - 1];
  const base = vals[0] || last || 1;
  const lookback = Math.max(2, Math.min(6, Math.floor(vals.length / 2)));
  const rawMom   = (last - vals[vals.length - lookback]) / (vals[vals.length - lookback] || 1);
  const span     = Math.max(...vals) - Math.min(...vals);
  const flatPct  = span / (base || 1);
  const isFlat   = flatPct < 0.0005;
  const dayDelta = (last - base) / base;
  const rg       = (regime?.regime || "").toUpperCase();
  const isBull   = regime?.ok && (rg.includes("BULL") || rg.includes("LOW_VOL"));
  const isBear   = regime?.ok && rg.includes("BEAR");
  const conf     = Math.min(regime?.confidence || 0.55, 1);
  const regimeMult = isFlat ? 2.8 : 1.0;
  const regimeBias = (isBull ? 1 : isBear ? -1 : 0) * conf * 0.0018 * regimeMult;
  const momComp  = Math.max(-0.008, Math.min(0.008, rawMom * 0.18));
  const dayBias  = Math.max(-0.004, Math.min(0.004, dayDelta * 0.12));
  const trend    = momComp + regimeBias + dayBias;
  const pts = [], upper = [], lower = [];
  let v = last;
  for (let i = 0; i < N; i++) {
    const t = (i + 1) / N;
    const osc = (Math.sin(t * Math.PI * 1.8 + 0.4) * 0.0022
              +  Math.sin(t * Math.PI * 3.2 + 1.1) * 0.0009) * last;
    v = v + v * trend + osc;
    const band = last * (0.004 + t * 0.016);
    pts.push(v);
    upper.push(v + band);
    lower.push(Math.max(v - band, last * 0.92));
  }
  return { pts, upper, lower };
}

// ─── Market bounds helper (ET) ───────────────────────────────────────────────
function getMarketBounds() {
  try {
    const now  = new Date();
    const etNow = new Date(now.toLocaleString("en-US", { timeZone:"America/New_York" }));
    const offset = now.getTime() - etNow.getTime();
    const midnight = new Date(etNow); midnight.setHours(0,0,0,0);
    const open  = midnight.getTime() +  9*3600000 + 30*60000 + offset;
    const close = midnight.getTime() + 16*3600000 + offset;
    return { open, close, dur: close - open };
  } catch {
    return { open: Date.now()-4*3600000, close: Date.now()+2*3600000, dur:6.5*3600000 };
  }
}

// ─── OmegaChart — account balance + projection ───────────────────────────────
export function OmegaChart({ tl = [], fc = [], live = [], outcomes = [], layers = {}, range = "1M", dpPos = true, regime = null, topPicks = [], feedEvents = [], liveWinStart = null, advancedMode = null, dark = true }) {
  const containerRef = useRef(null);
  const svgRef       = useRef(null);
  const [scrub, setScrub] = useState(null);

  const cTxt   = dark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.75)";
  const cMuted = dark ? "rgba(255,255,255,0.32)" : "rgba(0,0,0,0.42)";
  const cGrid  = dark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.10)";
  const cDot   = dark ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.85)";
  const cNow   = dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.14)";
  const cNowLbl= dark ? "rgba(255,255,255,0.32)" : "rgba(0,0,0,0.40)";

  const VW = 820, VH = 240;
  const PAD = { t: 24, r: 68, b: 32, l: 14 };
  const iW = VW - PAD.l - PAD.r;
  const iH = VH - PAD.t - PAD.b;
  const LIVE_WIN_MS = 15 * 60 * 1000;

  // ── Data source ─────────────────────────────────────────────────────────────
  const isLiveMode = range === "LIVE";
  const is1DMode   = range === "1D";
  const useTimeSrc = isLiveMode || is1DMode;
  const srcTl      = useTimeSrc ? [] : tl;

  const eqs   = useTimeSrc ? live.map(p => p.e) : srcTl.map(t => t.equity);
  const times = useTimeSrc
    ? live.map(p => p.t)
    : srcTl.map(t => {
        if (!t.date) return null;
        const d = new Date(t.date + "T12:00:00");
        return isNaN(d.getTime()) ? null : d.getTime();
      });
  const labels = useTimeSrc
    ? live.map(p => new Date(p.t).toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit", hour12:true }))
    : srcTl.map(t => {
        if (!t.date) return "";
        const d = new Date(t.date + "T12:00:00");
        if (isNaN(d.getTime())) return "";
        if (range === "1W") return d.toLocaleDateString("en-US", { weekday:"short" });
        if (range === "1M" || range === "3M") return d.toLocaleDateString("en-US", { month:"short", day:"numeric" });
        return d.toLocaleDateString("en-US", { month:"short", year:"2-digit" });
      });

  const N = eqs.length;

  // ── X mapping ────────────────────────────────────────────────────────────
  const mkt        = useTimeSrc ? getMarketBounds() : null;
  const validTimes = times.filter(t => t != null);
  const tMin       = validTimes[0] ?? 0;
  const tMax       = validTimes[validTimes.length - 1] ?? 1;
  const tSpan      = tMax - tMin || 1;

  function toX(i) {
    if (isLiveMode && liveWinStart && times[i])
      return PAD.l + Math.min(1, Math.max(0, (times[i] - liveWinStart) / LIVE_WIN_MS)) * iW;
    if (is1DMode && mkt && times[i])
      return PAD.l + Math.min(1, Math.max(0, (times[i] - mkt.open) / mkt.dur)) * iW;
    if (!useTimeSrc && validTimes.length > 1 && times[i] != null)
      return PAD.l + Math.min(1, Math.max(0, (times[i] - tMin) / tSpan)) * iW;
    return PAD.l + (N < 2 ? 0 : (i / (N - 1))) * iW;
  }

  // ── Projection ───────────────────────────────────────────────────────────
  const PRED_BY_RANGE = { LIVE:6, "1D":5, "1W":3, "1M":7, "3M":12, "1Y":20, "ALL":30 };
  const predN         = Math.min(N, PRED_BY_RANGE[range] ?? Math.max(5, Math.round(N * 0.15)));
  const { pts: predPts } = generateOmegaPrediction(eqs, regime, predN);

  let predExtend = 56;
  if (is1DMode && mkt && times.length > 0)
    predExtend = Math.max(20, (1 - Math.min(1, ((times[times.length-1] || Date.now()) - mkt.open) / mkt.dur)) * iW + 4);
  if (isLiveMode && liveWinStart && times.length > 0)
    predExtend = Math.max(20, (1 - Math.min(1, ((times[times.length-1] || Date.now()) - liveWinStart) / LIVE_WIN_MS)) * iW + 4);

  // ── Y bounds ─────────────────────────────────────────────────────────────
  const allY = [...eqs, ...predPts].filter(Boolean);
  if (allY.length < 2) {
    return (
      <div style={{ height:"100%", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:8 }}>
        <span style={{ fontSize:18, opacity:0.30 }}>◈</span>
        <span style={{ fontSize:11, color:"rgba(255,255,255,0.22)", textAlign:"center", maxWidth:180, lineHeight:1.5 }}>
          {isLiveMode ? "Live ticks stream during market hours" : is1DMode ? "Today's data loads at market open" : "No history for this range"}
        </span>
      </div>
    );
  }
  const rawMin = Math.min(...allY), rawMax = Math.max(...allY);
  const yPad   = (rawMax - rawMin) * 0.16 || 2;
  const minY   = rawMin - yPad, maxY = rawMax + yPad, rangeY = maxY - minY;
  const toY    = (v) => PAD.t + (1 - (v - minY) / rangeY) * iH;

  const nowX   = toX(N - 1);
  const nowY   = eqs[N - 1] != null ? toY(eqs[N - 1]) : PAD.t + iH / 2;
  const predXv = (i) => nowX + ((i + 1) / predN) * predExtend;

  // ── Portfolio path ────────────────────────────────────────────────────────
  const portPts  = eqs.map((v, i) => ({ x: toX(i), y: toY(v) }));
  const portPath = smoothPath(portPts);
  const portFill = portPts.length > 1
    ? `${portPath} L ${portPts[portPts.length-1].x.toFixed(1)},${(VH-PAD.b).toFixed(1)} L ${portPts[0].x.toFixed(1)},${(VH-PAD.b).toFixed(1)} Z`
    : "";
  const lineColor = dpPos ? "#30d158" : "#FF453A";

  // ── Projection path ───────────────────────────────────────────────────────
  const predXY     = predPts.map((v, i) => ({ x: predXv(i), y: toY(v) }));
  const predPath   = predXY.length > 0 ? smoothPath([{ x: nowX, y: nowY }, ...predXY]) : "";
  const predEndX   = predXY.length > 0 ? predXY[predXY.length-1].x : nowX;
  const predEndY   = predXY.length > 0 ? predXY[predXY.length-1].y : nowY;
  const predEndVal = predPts.length > 0 ? predPts[predPts.length-1] : (eqs[N-1] ?? 0);
  const predDelta  = ((predEndVal - (eqs[N-1] ?? 1)) / (eqs[N-1] || 1)) * 100;
  const predCol    = predDelta >= 0 ? "#30d158" : "#FF453A";

  // ── Grid + labels ────────────────────────────────────────────────────────
  const nGrid  = 3;
  const gridYs = Array.from({ length: nGrid + 1 }, (_, i) => minY + (rangeY / nGrid) * i);

  const MAX_X_LABELS = 5;
  const xStep  = Math.max(1, Math.ceil((N - 1) / Math.max(1, MAX_X_LABELS - 1)));
  const xLbls  = N > 0 ? labels.map((lbl, i) => ({ lbl, i })).filter(({ i }) => i % xStep === 0 || i === N - 1) : [];

  const liveXLabels = [];
  if ((isLiveMode || is1DMode) && mkt) {
    const base  = isLiveMode ? (liveWinStart || (times[0] ?? mkt.open)) : mkt.open;
    const span  = isLiveMode ? LIVE_WIN_MS : mkt.dur;
    const ticks = isLiveMode ? [0, 5, 10, 15] : [1.5, 3, 4.5];
    const mult  = isLiveMode ? 60000 : 3600000;
    ticks.forEach(t => {
      const ts = base + t * mult;
      liveXLabels.push({
        x:   PAD.l + Math.min(1, Math.max(0, (ts - base) / span)) * iW,
        lbl: new Date(ts).toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit", hour12:true }),
      });
    });
  }

  // ── LIVE sweep + scrubber ─────────────────────────────────────────────────
  const sweepFrac  = isLiveMode && liveWinStart ? Math.min(1, (Date.now() - liveWinStart) / LIVE_WIN_MS) : null;
  const handleScrub = (e) => {
    if (!svgRef.current || !N) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    // Use the SVG element's own rect for exact pixel→SVG mapping
    const rect = svgRef.current.getBoundingClientRect();
    const svgX = Math.max(PAD.l, Math.min(PAD.l + iW, (clientX - rect.left) * (VW / rect.width)));
    // Find nearest data point by actual x distance (handles non-uniform date spacing)
    let bestIdx = 0, bestDist = Infinity;
    for (let i = 0; i < N; i++) {
      const d = Math.abs(toX(i) - svgX);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    }
    const price = eqs[bestIdx];
    if (price == null) return;
    const baseEq = eqs[0] ?? price;
    const change = price - baseEq;
    const pct    = (change / (baseEq || 1)) * 100;
    // crosshair follows cursor exactly; price info from nearest data point
    setScrub({ price, lbl: labels[bestIdx], x: svgX, y: toY(price), change, pct });
  };

  return (
    <div
      ref={containerRef}
      style={{ width:"100%", height:"100%", position:"relative", userSelect:"none", WebkitUserSelect:"none", display:"flex", flexDirection:"column" }}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VW} ${VH}`}
        style={{ width:"100%", flex:1, display:"block", cursor: N ? "crosshair" : "default" }}
        preserveAspectRatio="none"
        onMouseMove={handleScrub}
        onMouseLeave={() => setScrub(null)}
        onTouchMove={e => { e.preventDefault(); handleScrub(e); }}
        onTouchEnd={() => setScrub(null)}
      >
        <defs>
          <linearGradient id="ocFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={lineColor} stopOpacity="0.20"/>
            <stop offset="80%"  stopColor={lineColor} stopOpacity="0.02"/>
            <stop offset="100%" stopColor={lineColor} stopOpacity="0.00"/>
          </linearGradient>
          <linearGradient id="ocProjFade" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor={predCol} stopOpacity="0.07"/>
            <stop offset="100%" stopColor={predCol} stopOpacity="0.00"/>
          </linearGradient>
          <filter id="ocGlow" x="-20%" y="-80%" width="140%" height="260%">
            <feGaussianBlur stdDeviation="3.5" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        {/* Horizontal grid lines */}
        {gridYs.map((gv, gi) => (
          <line key={gi}
            x1={PAD.l} y1={toY(gv).toFixed(1)} x2={VW - PAD.r} y2={toY(gv).toFixed(1)}
            stroke={cGrid} strokeWidth="1"
            strokeDasharray={gi > 0 && gi < nGrid ? "3,10" : "none"}/>
        ))}

        {/* Projection zone tint */}
        {predXY.length > 0 && (
          <rect x={nowX.toFixed(1)} y={PAD.t}
            width={(predEndX - nowX + 6).toFixed(1)} height={iH}
            fill="url(#ocProjFade)"/>
        )}

        {/* Account balance gradient fill */}
        {portFill && <path d={portFill} fill="url(#ocFill)"/>}

        {/* Account balance glow */}
        {portPath && (
          <path d={portPath} fill="none" filter="url(#ocGlow)"
            stroke={lineColor} strokeWidth="7"
            strokeLinecap="round" strokeLinejoin="round" opacity="0.28"/>
        )}

        {/* Account balance line */}
        {portPath && (
          <path d={portPath} fill="none"
            stroke={lineColor} strokeWidth="2.4"
            strokeLinecap="round" strokeLinejoin="round"/>
        )}

        {/* NOW divider */}
        <line x1={nowX.toFixed(1)} y1={PAD.t} x2={nowX.toFixed(1)} y2={VH - PAD.b}
          stroke={cNow} strokeWidth="1" strokeDasharray="3,8"/>
        <text x={(nowX + 4).toFixed(1)} y={(PAD.t + 10).toFixed(1)}
          fill={cNowLbl} fontSize="7.5" fontWeight="700" letterSpacing="0.8"
          fontFamily="-apple-system,sans-serif">NOW</text>

        {/* Projection dotted line */}
        {predPath && (
          <path d={predPath} fill="none"
            stroke={predCol} strokeWidth="2" strokeLinecap="round"
            strokeDasharray="6,5" opacity="0.75"/>
        )}

        {/* Projection % label */}
        {predXY.length > 0 && (
          <text
            x={(predEndX + 5).toFixed(1)} y={(predEndY + 4).toFixed(1)}
            fontSize="9" fontWeight="800" fill={predCol}
            fontFamily="-apple-system,sans-serif"
            textAnchor={predEndX > VW - PAD.r - 44 ? "end" : "start"}>
            {predDelta >= 0 ? "+" : ""}{predDelta.toFixed(1)}%
          </text>
        )}

        {/* Y-axis price labels */}
        {gridYs.map((gv, gi) => (
          <text key={gi}
            x={(VW - PAD.r + 7).toFixed(1)} y={(toY(gv) + 4).toFixed(1)}
            fill={cMuted} fontSize="9" fontWeight="600"
            fontFamily="-apple-system,sans-serif">
            ${gv >= 1000 ? (gv / 1000).toFixed(1) + "k" : gv.toFixed(0)}
          </text>
        ))}

        {/* X-axis labels */}
        {(isLiveMode || is1DMode) ? liveXLabels.map((lb, i) => (
          <text key={i} x={lb.x.toFixed(1)} y={(VH - 7).toFixed(1)}
            fill={cMuted} fontSize="8" textAnchor="middle"
            fontFamily="-apple-system,sans-serif">{lb.lbl}</text>
        )) : xLbls.map(({ lbl, i }) => (
          <text key={i} x={toX(i).toFixed(1)} y={(VH - 7).toFixed(1)}
            fill={cMuted} fontSize="8" textAnchor="middle"
            fontFamily="-apple-system,sans-serif">{lbl}</text>
        ))}

        {/* 1D Open / Close edge labels */}
        {is1DMode && (
          <g>
            <text x={PAD.l.toFixed(1)} y={(VH - 7).toFixed(1)}
              fill={cMuted} fontSize="8"
              fontFamily="-apple-system,sans-serif">Open</text>
            <text x={(VW - PAD.r).toFixed(1)} y={(VH - 7).toFixed(1)}
              textAnchor="end" fill="rgba(255,255,255,0.18)" fontSize="8"
              fontFamily="-apple-system,sans-serif">Close</text>
          </g>
        )}

        {/* LIVE sweep bar */}
        {isLiveMode && sweepFrac !== null && (
          <g>
            <rect x={PAD.l} y={VH - PAD.b + 10} width={iW} height="2" rx="1" fill={cGrid}/>
            <rect x={PAD.l} y={VH - PAD.b + 10}
              width={(sweepFrac * iW).toFixed(1)} height="2" rx="1" fill="#60a5fa" opacity="0.55"/>
          </g>
        )}

        {/* Scrubber crosshair + dot */}
        {scrub && (
          <g>
            <line x1={scrub.x.toFixed(1)} y1={PAD.t} x2={scrub.x.toFixed(1)} y2={VH - PAD.b}
              stroke={cMuted} strokeWidth="1"/>
            <circle cx={scrub.x.toFixed(1)} cy={scrub.y.toFixed(1)} r="7"
              fill="none" stroke={cMuted} strokeWidth="1.5"/>
            <circle cx={scrub.x.toFixed(1)} cy={scrub.y.toFixed(1)} r="3.5"
              fill={cDot} opacity="0.92"/>
          </g>
        )}
      </svg>

      {/* Slide-up scrubber panel */}
      <div style={{
        position:"absolute", bottom:0, left:0, right:0, height:64,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"0 20px",
        background:"rgba(6,8,22,0.93)",
        backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)",
        borderTop:"1px solid rgba(255,255,255,0.08)",
        transform: scrub ? "translateY(0)" : "translateY(100%)",
        transition:"transform 200ms cubic-bezier(0.22,1,0.36,1)",
        pointerEvents:"none", zIndex:5,
      }}>
        <span style={{ fontSize:14, color:"rgba(255,255,255,0.40)", fontWeight:600, minWidth:72 }}>
          {scrub?.lbl ?? ""}
        </span>
        <span style={{ fontSize:28, color:"#fff", fontWeight:800, letterSpacing:-1 }}>
          ${scrub ? scrub.price.toFixed(2) : "—"}
        </span>
        {scrub && (
          <div style={{ textAlign:"right", minWidth:88 }}>
            <div style={{ fontSize:17, fontWeight:700, lineHeight:1.2,
              color: scrub.change >= 0 ? "#30d158" : "#FF453A" }}>
              {scrub.change >= 0 ? "+" : ""}{scrub.change.toFixed(2)}
            </div>
            <div style={{ fontSize:13, fontWeight:600, lineHeight:1.2, opacity:0.85,
              color: scrub.change >= 0 ? "#30d158" : "#FF453A" }}>
              {scrub.pct >= 0 ? "+" : ""}{scrub.pct.toFixed(2)}%
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Mini sparkline (SVG, no dependencies) ───────────────────────────────────
function Spark({ values = [], color = C.blue, h = 44, w = "100%" }) {
  if (values.length < 2) return <div style={{ height: h }} />;
  const min = Math.min(...values), max = Math.max(...values), range = max - min || 1;
  const W = 200, H = h;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W;
    const y = H - ((v - min) / range) * (H - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const last = values[values.length - 1];
  const prev = values[values.length - 2] ?? last;
  const up = last >= prev;
  const fillColor = up ? C.green : C.red;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: w, height: h, display: "block" }} preserveAspectRatio="none">
      <defs>
        <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fillColor} stopOpacity="0.22" />
          <stop offset="100%" stopColor={fillColor} stopOpacity="0.00" />
        </linearGradient>
      </defs>
      <polyline fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" points={pts} />
    </svg>
  );
}

// ─── Info Card component ─────────────────────────────────────────────────────
function InfoCard({ icon, label, value, sub, color = C.blue, onClick, badge, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      style={{
        display: "block", width: "100%", textAlign: "left", fontFamily: "inherit",
        padding: "13px 13px 11px", borderRadius: 18,
        background: `${color}0d`,
        border: `1px solid ${color}28`,
        cursor: onClick ? "pointer" : "default",
        position: "relative", overflow: "hidden",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* glow corner */}
      <div style={{ position:"absolute", top:-18, right:-18, width:60, height:60, borderRadius:"50%", background:`${color}18`, filter:"blur(16px)", pointerEvents:"none" }} />
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:7 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <span style={{ fontSize:13, lineHeight:1 }}>{icon}</span>
          <span style={{ fontSize:9, fontWeight:800, letterSpacing:1.4, color:`${color}cc`, textTransform:"uppercase" }}>{label}</span>
        </div>
        {badge && (
          <span style={{ fontSize:8, fontWeight:900, letterSpacing:1, padding:"2px 6px", borderRadius:5, background:`${color}22`, color, border:`1px solid ${color}44` }}>
            {badge}
          </span>
        )}
      </div>
      <div style={{ fontSize:20, fontWeight:800, color:"#fff", lineHeight:1.1, marginBottom:3 }}>{value}</div>
      {sub && <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", lineHeight:1.4 }}>{sub}</div>}
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
export function AccountExpanded() {
  // ─── State ─────────────────────────────────────────────────────────────────
  const [data,        setData]        = useState(null);
  const [pnl,         setPnl]         = useState(null);
  const [watch,       setWatch]       = useState(null);
  const [bpower,      setBpower]      = useState(null);
  const [apStatus,    setApStatus]    = useState(null);   // autopilot status
  const [apPositions, setApPositions] = useState([]);     // autopilot open positions
  const [outcomes,    setOutcomes]    = useState([]);     // trade outcomes (signals)
  const [pdtInfo,     setPdtInfo]     = useState(null);   // day trade info
  const [goals,       setGoals]       = useState([]);
  const [insight,     setInsight]     = useState("");
  const [live,        setLive]        = useState([]);
  const [range,       setRange]       = useState("1M");
  const [rangePnlApi, setRangePnlApi] = useState(null);
  const [layers,      setLayers]      = useState(DEFAULT_LAYERS);
  const [lifeCard,    setLifeCard]    = useState(null);
  const [showLegend,  setShowLegend]  = useState(false);

  // ─── Data fetching ──────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/account/life-chart`),
      fastGetAsync(`${API}/daily-pnl?period=month`),
      fastGetAsync(`${API}/omega/watch`),
      axios.get(`${API}/preload`),
      axios.get(`${API}/omega/status`),
      axios.get(`${API}/autopilot/positions`),
      axios.get(`${API}/autopilot/outcomes`),
      axios.get(`${API}/pdt/status`),
      axios.get(`${API}/goals`),
    ]).then(([lc, dp, wr, pr, aps, app, apo, pdt, gl]) => {
      setData(lc.data);
      setPnl(dp.data);
      setWatch(wr.data);
      const pf = pr.data?.portfolio || {};
      setBpower({ buyingPower: pf.buyingPower || 0, cashBuyingPower: pf.cashBuyingPower || 0, optionsBuyingPower: pf.optionsBuyingPower || 0 });
      setApStatus(aps.data);
      setApPositions((app.data || []).filter(p => p.status === "open"));
      setOutcomes(Array.isArray(apo.data) ? apo.data : []);
      setPdtInfo(pdt.data || null);
      setGoals(gl.data || []);
    }).catch(() => {});

    const iv = setInterval(() => {
      fastGetAsync(`${API}/daily-pnl`).then(r => {
        setPnl(p => {
          if (p?.totalEquity === r.data.totalEquity && p?.totalDayPnl === r.data.totalDayPnl) return p;
          return { ...p, totalDayPnl: r.data.totalDayPnl, dayPnlPct: r.data.dayPnlPct, totalEquity: r.data.totalEquity };
        });
        setLive(prev => {
          const last = prev[prev.length - 1];
          if (last && Math.abs(last.e - r.data.totalEquity) < 0.01) return prev;
          return [...prev, { t: Date.now(), e: r.data.totalEquity }].slice(-300);
        });
      }).catch(() => {});
    }, 15000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const map = { "1D":"day","1W":"week","1M":"month","3M":"quarter","1Y":"year","ALL":"all" };
    fastGetAsync(`${API}/daily-pnl?period=${map[range]}`).then(r => setRangePnlApi(r.data)).catch(() => setRangePnlApi(null));
  }, [range]);

  useEffect(() => {
    if (!data || insight) return;
    const eq = pnl?.totalEquity || data?.currentEquity || 0;
    const fc = data?.forecast || [];
    const p  = `Equity $${eq.toFixed(2)}, Day ${sign(pnl?.totalDayPnl || 0)}$${(pnl?.totalDayPnl || 0).toFixed(2)}, ${data?.positions?.length || 0} positions, forecast ${fc.length > 0 ? "$" + fc[fc.length-1].equity : "flat"}. 2 sentences: status + outlook.`;
    axios.post(`${API}/zi/chat`, { message: p, sessionId: `acct-${Date.now()}` })
      .then(r => setInsight(r.data?.response || ""))
      .catch(() => setInsight(`Portfolio at ${f$(eq)}. ${(pnl?.totalDayPnl || 0) >= 0 ? "Positive momentum today." : "Risk gate monitoring — holding steady."}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pnl]);

  // ─── Derived values ─────────────────────────────────────────────────────────
  const eq          = pnl?.totalEquity || data?.currentEquity || 0;
  const tl          = data?.timeline || [];
  const fc          = data?.forecast || [];
  const dpAmt       = pnl?.totalDayPnl || 0;
  const dpPos       = dpAmt >= 0;
  const dpColor     = dpPos ? C.green : C.red;
  const startEq     = tl.length > 0 ? tl[0].equity : eq;
  const totalReturn = pct(eq, startEq);

  const dayChanges = tl.length > 1 ? tl.map((t, i) => {
    if (i === 0 || !tl[i-1]?.equity) return null;
    const d = t.equity - tl[i-1].equity;
    return { date: t.date, pct: pct(t.equity, tl[i-1].equity), delta: d, eq: t.equity };
  }).filter(Boolean) : [];

  const bestDay  = dayChanges.length ? dayChanges.reduce((a, b) => b.pct > a.pct ? b : a) : null;
  const worstDay = dayChanges.length ? dayChanges.reduce((a, b) => b.pct < a.pct ? b : a) : null;
  const wins30   = dayChanges.slice(-30).filter(d => d.pct > 0).length;
  const winRate  = dayChanges.slice(-30).length ? Math.round(wins30 / Math.min(dayChanges.length, 30) * 100) : 0;

  // Range filtering
  const rangeDays    = { "1D":1,"1W":7,"1M":30,"3M":90,"1Y":365,"ALL":99999 }[range] ?? 99999;
  const filteredTl   = range === "1D" ? [] : tl.slice(Math.max(0, tl.length - rangeDays));
  const rangeStart   = filteredTl[0]?.equity ?? startEq;
  const rangePnlRaw  = rangePnlApi?.periodPnl ?? (eq - rangeStart);
  const rangePctRaw  = rangeStart > 0 ? pct(eq, rangeStart) : 0;

  // Goals summary
  const goalsDone   = goals.filter(g => (g.progress || 0) >= 100).length;
  const goalsActive = goals.filter(g => (g.progress || 0) < 100).length;

  // Positions
  const posCount     = watch?.count || data?.positions?.length || 0;
  const pendingCount = apPositions.filter(p => p.status === "pending" || p.status === "open").length;

  // chartData removed — replaced by OmegaChart SVG component

  // ─── Skeleton ───────────────────────────────────────────────────────────────
  if (!data && !pnl) return (
    <div style={{ padding:"0 16px 32px" }}>
      <Skel w="100%" h={220} mb={14} radius={24} />
      <Skel w="100%" h={280} mb={14} radius={20} />
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14 }}>
        {Array.from({length:6}).map((_,i) => <Skel key={i} w="100%" h={90} radius={18} />)}
      </div>
    </div>
  );

  // ─── openLife (tap-to-expand popups) ────────────────────────────────────────
  const openLife = (id) => {
    if (id === "today") return setLifeCard({ id, title:"Today · Intraday P&L", accent: dpColor,
      content: (
        <div>
          <div style={{ fontSize:32, fontWeight:800, color:dpColor }}>{sign(dpAmt)}{f$(dpAmt)}</div>
          <div style={{ fontSize:14, color:"rgba(255,255,255,0.45)", marginBottom:16 }}>{sign(pnl?.dayPnlPct || 0)}{fN(pnl?.dayPnlPct,2)}% · {f$(eq)}</div>
          {live.length > 2 ? <Spark values={live.map(p=>p.e)} color={dpColor} h={80} /> : <div style={{fontSize:12,color:"rgba(255,255,255,0.3)",padding:"20px 0"}}>Live ticks stream during market hours.</div>}
        </div>
      ),
    });
    if (id === "totalReturn") return setLifeCard({ id, title:"Total Return · Life of Account", accent: totalReturn>=0 ? C.green : C.red,
      content: (
        <div>
          <div style={{ fontSize:32, fontWeight:800, color: totalReturn>=0?C.green:C.red }}>{sign(totalReturn)}{fN(totalReturn,1)}%</div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.40)", marginBottom:16 }}>{f$(startEq)} → {f$(eq)}</div>
          <div style={{ height:160 }}>
            <OmegaChart tl={tl} fc={fc} live={live} outcomes={outcomes} layers={layers} range={range} dpPos={dpPos}/>
          </div>
        </div>
      ),
    });
    if (id === "positions") return setLifeCard({ id, title:"Open Positions", accent: C.green,
      content: (
        <div>
          <div style={{ fontSize:32, fontWeight:800 }}>{posCount}</div>
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", marginBottom:14 }}>active trades</div>
          {(watch?.positions || []).slice(0,10).map((p,i) => (
            <div key={i} style={{ display:"flex",justifyContent:"space-between",padding:"9px 0",borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
              <span style={{fontSize:13,fontWeight:700}}>{p.ticker}{p.type==="OPTION"?` ${p.strike}${p.side?.[0]||""}`:""}</span>
              <span style={{fontSize:13,fontWeight:800,color:(p.dayGainPct||0)>=0?C.green:C.red}}>{sign(p.dayGainPct||0)}{fN(p.dayGainPct,1)}%</span>
            </div>
          ))}
        </div>
      ),
    });
  };

  const accentHero = C.sky;
  const apMode     = apStatus?.autopilotMode || "—";
  const apOn       = apMode !== "OFF" && apMode !== "—" && !apStatus?.killSwitches?.anyFired;

  return (
    <div style={{ padding:"0 0 40px" }}>

      {/* ═══════ HERO ══════════════════════════════════════════════════════════ */}
      <div style={{
        margin:"0 0 12px",
        borderRadius:28,
        background:"linear-gradient(160deg, #0d1f3c 0%, #080c14 65%)",
        border:"1px solid rgba(56,189,248,0.18)",
        overflow:"hidden", position:"relative",
        boxShadow:"0 20px 60px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
      }}>
        {/* Deep glow */}
        <div style={{ position:"absolute",top:-60,left:"50%",transform:"translateX(-50%)",width:260,height:260,borderRadius:"50%",background:"radial-gradient(circle, rgba(56,189,248,0.12) 0%, transparent 70%)",pointerEvents:"none" }} />

        {/* Top strip */}
        <div style={{ height:2, background:`linear-gradient(90deg, transparent, ${accentHero}88, transparent)` }} />

        <div style={{ padding:"20px 18px 0" }}>
          {/* Row: label + mode badge */}
          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6 }}>
            <div style={{ display:"flex",alignItems:"center",gap:8 }}>
              <span style={{ width:7,height:7,borderRadius:"50%",background:accentHero,boxShadow:`0 0 10px ${accentHero}` }} />
              <span style={{ fontSize:9,fontWeight:900,letterSpacing:2.5,color:accentHero,textTransform:"uppercase" }}>Total Equity</span>
            </div>
            <div style={{ display:"flex",alignItems:"center",gap:6 }}>
              {apOn && (
                <span style={{ fontSize:8,fontWeight:900,letterSpacing:1.2,padding:"3px 8px",borderRadius:6,background:"rgba(74,222,128,0.15)",color:C.green,border:`1px solid ${C.green}44` }}>
                  Ω {apMode.replace("_"," ")}
                </span>
              )}
              <span style={{ fontSize:8,fontWeight:900,letterSpacing:1.2,padding:"3px 8px",borderRadius:6,background:"rgba(255,255,255,0.06)",color:"rgba(255,255,255,0.40)",border:"1px solid rgba(255,255,255,0.08)" }}>
                {new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"})}
              </span>
            </div>
          </div>

          {/* Equity number */}
          <div style={{ fontSize:52,fontWeight:900,color:"#fff",letterSpacing:-2,lineHeight:1,marginBottom:10 }}>
            {f$(eq)}
          </div>

          {/* Day P&L + return pills */}
          <div style={{ display:"flex",gap:8,flexWrap:"wrap",marginBottom:14 }}>
            <div style={{
              display:"flex",alignItems:"center",gap:5,
              padding:"6px 12px",borderRadius:10,
              background: dpPos ? "rgba(74,222,128,0.14)" : "rgba(248,113,113,0.14)",
              border:`1px solid ${dpColor}44`,
            }}>
              <span style={{fontSize:12,color:dpColor}}>{dpPos?"▲":"▼"}</span>
              <span style={{fontSize:13,fontWeight:800,color:dpColor}}>{sign(dpAmt)}{f$(dpAmt)}</span>
              <span style={{fontSize:11,color:`${dpColor}bb`}}>({sign(pnl?.dayPnlPct||0)}{fN(pnl?.dayPnlPct,2)}%)</span>
            </div>
            <div style={{
              padding:"6px 12px",borderRadius:10,
              background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.08)",
              fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.55)",
            }}>
              All-time {sign(totalReturn)}{fN(totalReturn,1)}%
            </div>
          </div>

          {/* Asset breakdown */}
          {pnl?.breakdown && (() => {
            const slices = [
              {k:"equity",label:"Stock",dot:C.blue},
              {k:"option",label:"Options",dot:C.purple},
              {k:"crypto",label:"Crypto",dot:C.gold},
            ].filter(s => (pnl.breakdown[s.k]?.count||0) > 0);
            if (!slices.length) return null;
            return (
              <div style={{display:"flex",gap:6,marginBottom:14}}>
                {slices.map(s => {
                  const v = pnl.breakdown[s.k]||{}; const pos=(v.dayPnl||0)>=0;
                  return (
                    <div key={s.k} style={{flex:1,padding:"8px 10px",borderRadius:12,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.07)"}}>
                      <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:2}}>
                        <span style={{width:5,height:5,borderRadius:"50%",background:s.dot,flexShrink:0}}/>
                        <span style={{fontSize:8,letterSpacing:1,color:"rgba(255,255,255,0.50)",fontWeight:700}}>{s.label} ·{v.count}</span>
                      </div>
                      <div style={{fontSize:13,fontWeight:800,color:pos?C.green:C.red}}>{pos?"+":""}{f$(v.dayPnl)}</div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Inline sparkline — "infinity" look */}
        <div style={{ position:"relative", height:64, overflow:"hidden" }}>
          {/* fade edges */}
          <div style={{position:"absolute",top:0,left:0,width:48,height:"100%",background:"linear-gradient(90deg,#080c14,transparent)",zIndex:2,pointerEvents:"none"}}/>
          <div style={{position:"absolute",top:0,right:0,width:48,height:"100%",background:"linear-gradient(270deg,#080c14,transparent)",zIndex:2,pointerEvents:"none"}}/>
          <Spark values={tl.slice(-60).map(t=>t.equity)} color={accentHero} h={64} w="100%" />
        </div>

        {/* Quick stat tiles */}
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:1,borderTop:"1px solid rgba(255,255,255,0.05)" }}>
          {[
            { id:"today",   label:"Today",    val:f$(dpAmt),       color:dpColor,       pos:dpPos },
            { id:"month",   label:"Month",    val:f$(pnl?.periodPnl), color:(pnl?.periodPnl||0)>=0?C.green:C.red, pos:(pnl?.periodPnl||0)>=0 },
            { id:"ytd",     label:"Win Rate", val:`${winRate}%`,   color:winRate>=50?C.green:C.red, pos:winRate>=50 },
          ].map((s,i) => (
            <button key={s.id} onClick={() => { hapticTap(); if(s.id!=="ytd") openLife(s.id); }} style={{
              padding:"12px 10px", background:"none", border:"none",
              borderRight: i<2?"1px solid rgba(255,255,255,0.05)":"none",
              cursor:"pointer", fontFamily:"inherit", textAlign:"left",
            }}>
              <div style={{fontSize:8,letterSpacing:1.5,color:"rgba(255,255,255,0.30)",fontWeight:700,marginBottom:4,textTransform:"uppercase"}}>{s.label}</div>
              <div style={{fontSize:15,fontWeight:800,color:s.color}}>{s.pos&&s.id!=="ytd"?"+":""}{s.val}</div>
            </button>
          ))}
        </div>
      </div>

      {/* ═══════ CHART CARD ════════════════════════════════════════════════════ */}
      <div style={{ marginBottom:12, borderRadius:22, background:"rgba(8,12,20,0.90)", border:"1px solid rgba(255,255,255,0.07)", overflow:"hidden" }}>
        {/* Range tabs */}
        <div style={{ display:"flex",gap:2,padding:"10px 10px 0", overflowX:"auto" }}>
          {["1D","1W","1M","3M","1Y","ALL"].map(r => (
            <button key={r} data-testid={`range-${r}`} onClick={() => { soundTick(); setRange(r); }} style={{
              flexShrink:0, padding:"7px 13px", borderRadius:10, border:"none", cursor:"pointer",
              background: range===r ? `${C.blue}22` : "transparent",
              color: range===r ? C.blue : "rgba(255,255,255,0.30)",
              fontSize:11, fontWeight: range===r?800:500, fontFamily:"inherit",
              transition:"all 150ms",
            }}>{r}</button>
          ))}
          <div style={{flex:1}}/>
          <button onClick={()=>setShowLegend(v=>!v)} style={{
            flexShrink:0, padding:"7px 11px", borderRadius:10, border:`1px solid rgba(255,255,255,0.08)`,
            background: showLegend?"rgba(255,255,255,0.06)":"transparent",
            color:"rgba(255,255,255,0.45)", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
          }}>
            {showLegend?"✕ Layers":"⚙ Layers"}
          </button>
        </div>

        {/* P&L label */}
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 14px 4px" }}>
          <span style={{ fontSize:9,letterSpacing:1.5,color:"rgba(255,255,255,0.28)",fontWeight:700,textTransform:"uppercase" }}>
            {range==="1D" ? `Live · ${live.length} ticks` : `${range} P&L`}
          </span>
          <span style={{ fontSize:12,fontWeight:800,color:rangePnlRaw>=0?C.green:C.red }}>
            {sign(rangePnlRaw)}{f$(rangePnlRaw)} ({sign(rangePctRaw)}{fN(rangePctRaw,2)}%)
          </span>
        </div>

        {/* Legend toggles */}
        {showLegend && (
          <div style={{ display:"flex",gap:6,overflowX:"auto",padding:"6px 12px 8px",borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
            {LAYERS.map(l => (
              <button key={l.id} onClick={() => setLayers(v => ({...v,[l.id]:!v[l.id]}))} style={{
                flexShrink:0, display:"flex",alignItems:"center",gap:5,
                padding:"5px 10px", borderRadius:8,
                background: layers[l.id] ? `${l.color}22` : "rgba(255,255,255,0.03)",
                border:`1px solid ${layers[l.id] ? l.color+"55" : "rgba(255,255,255,0.06)"}`,
                color: layers[l.id] ? l.color : "rgba(255,255,255,0.25)",
                fontSize:10, fontWeight:layers[l.id]?800:500, cursor:"pointer", fontFamily:"inherit",
                transition:"all 150ms",
              }}>
                <span style={{fontSize:9}}>{l.icon}</span>
                <span>{l.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Chart — OmegaChart SVG */}
        <div style={{ padding:"4px 6px 10px", height:240, position:"relative" }}>
          <OmegaChart
            tl={filteredTl.length > 0 ? filteredTl : tl}
            fc={fc}
            live={live}
            outcomes={outcomes}
            layers={layers}
            range={range}
            dpPos={dpPos}
          />
        </div>
      </div>

      {/* ═══════ QUICK STAT SCROLL ROW ════════════════════════════════════════ */}
      <div style={{ display:"flex",gap:8,overflowX:"auto",marginBottom:12,paddingBottom:2 }}>
        {[
          { label:"Best Day",   val:`+${fN(bestDay?.pct||0,1)}%`,  color:C.green,  sub:bestDay?.date||"—"  },
          { label:"Worst Day",  val:`${fN(worstDay?.pct||0,1)}%`,  color:C.red,    sub:worstDay?.date||"—" },
          { label:"Win Rate",   val:`${winRate}%`,                  color:winRate>=50?C.green:C.red, sub:`last 30 days` },
          { label:"Total Return",val:`${sign(totalReturn)}${fN(totalReturn,1)}%`, color:totalReturn>=0?C.green:C.red, sub:`since start` },
        ].map((s,i) => (
          <div key={i} style={{
            flexShrink:0, minWidth:110, padding:"11px 13px", borderRadius:16,
            background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)",
          }}>
            <div style={{fontSize:8,letterSpacing:1.5,color:"rgba(255,255,255,0.30)",fontWeight:700,textTransform:"uppercase",marginBottom:4}}>{s.label}</div>
            <div style={{fontSize:17,fontWeight:800,color:s.color,marginBottom:2}}>{s.val}</div>
            <div style={{fontSize:9,color:"rgba(255,255,255,0.25)"}}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ═══════ INFO CARDS GRID ══════════════════════════════════════════════ */}
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12 }}>

        {/* Active Positions */}
        <InfoCard
          testId="card-positions"
          icon="📂" label="Active Positions" color={C.green}
          value={posCount}
          sub={posCount===0 ? "No open trades" : `${posCount} live trade${posCount!==1?"s":""}`}
          badge={posCount>0?"LIVE":undefined}
          onClick={() => { hapticTap(); openLife("positions"); }}
        >
          {posCount > 0 && (
            <div style={{ display:"flex",gap:4,marginTop:8,flexWrap:"wrap" }}>
              {(watch?.positions||[]).slice(0,4).map((p,i) => (
                <span key={i} style={{fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:5,background:`${C.green}18`,color:C.green}}>
                  {p.ticker}
                </span>
              ))}
            </div>
          )}
        </InfoCard>

        {/* Autopilot */}
        <InfoCard
          testId="card-autopilot"
          icon="🤖" label="Autopilot" color={apOn?C.green:C.purple}
          value={apMode.replace("_"," ")}
          sub={apOn ? `Autonomy: ${apStatus?.autonomyLevel||"—"}` : "Autopilot off"}
          badge={apOn?"ACTIVE":"OFF"}
        >
          {apStatus?.drift?.severity && apStatus.drift.severity !== "none" && (
            <div style={{ marginTop:6, fontSize:9, color:C.gold, fontWeight:700 }}>
              ⚠ Drift: {apStatus.drift.severity.toUpperCase()}
            </div>
          )}
        </InfoCard>

        {/* Buying Power */}
        <InfoCard
          testId="card-buying-power"
          icon="💰" label="Buying Power" color={C.sky}
          value={f$(bpower?.buyingPower)}
          sub={`Opts ${f$(bpower?.optionsBuyingPower)}`}
          badge="AVAILABLE"
        />

        {/* USD Real */}
        <InfoCard
          testId="card-usd-real"
          icon="💵" label="USD Real P&L" color={(pnl?.usdPnl||0)>=0?C.green:C.red}
          value={`${sign(pnl?.usdPnl||0)}${f$(pnl?.usdPnl)}`}
          sub="Real-money ledger"
        />

        {/* Day Trades */}
        <InfoCard
          testId="card-day-trades"
          icon="⚡" label="Day Trades" color={C.orange}
          value={pdtInfo ? `${pdtInfo.dayTradesRemaining ?? 3} left` : "—"}
          sub={pdtInfo ? `${pdtInfo.dayTradesUsed||0}/3 used · resets ${pdtInfo.resetDate||"weekly"}` : "Loading PDT status"}
          badge={pdtInfo?.pdtExempt ? "EXEMPT" : pdtInfo?.pdtWarning ? "⚠ WARN" : undefined}
        />

        {/* Omega AI Signal */}
        <InfoCard
          testId="card-omega-signal"
          icon="Ω" label="Omega AI Signal" color={C.purple}
          value={apStatus?.drift?.recentWinRate != null ? `${apStatus.drift.recentWinRate}%` : "—"}
          sub={`Win rate · ${outcomes.filter(o=>o.won).length}W / ${outcomes.filter(o=>!o.won).length}L`}
          badge={apStatus?.drift?.severity === "severe" ? "GEM-ONLY" : "LIVE"}
        >
          {outcomes.length > 0 && (
            <div style={{ display:"flex",gap:2,marginTop:8 }}>
              {outcomes.slice(-12).map((o,i) => (
                <div key={i} style={{ flex:1,height:5,borderRadius:2,background:o.won?C.green:C.red,opacity:0.80 }} />
              ))}
            </div>
          )}
        </InfoCard>

        {/* Intel / Watch Room */}
        <InfoCard
          testId="card-intel"
          icon="🧠" label="AI Watch Room" color={C.cyan}
          value={`${watch?.count||0} watching`}
          sub={`Omega scans ${watch?.tickers?.length||0} tickers`}
        />

        {/* Goals */}
        <InfoCard
          testId="card-goals"
          icon="🎯" label="Goals" color={C.amber}
          value={`${goalsDone}/${goals.length}`}
          sub={goalsActive > 0 ? `${goalsActive} active goal${goalsActive!==1?"s":""}` : goals.length===0?"No goals set":"All complete!"}
        >
          {goals.length > 0 && (() => {
            const g = goals.find(g=>(g.progress||0)<100) || goals[0];
            const p = Math.min(100, g?.progress||0);
            return (
              <div style={{ marginTop:8 }}>
                <div style={{ display:"flex",justifyContent:"space-between",marginBottom:3,fontSize:9,color:"rgba(255,255,255,0.35)" }}>
                  <span>{g.name}</span><span>{p.toFixed(0)}%</span>
                </div>
                <div style={{ height:4,borderRadius:999,background:"rgba(255,255,255,0.07)",overflow:"hidden" }}>
                  <div style={{ width:`${p}%`,height:"100%",background:`linear-gradient(90deg,${C.amber},${C.gold})`,transition:"width 400ms" }} />
                </div>
              </div>
            );
          })()}
        </InfoCard>

        {/* Pending / Autopilot Positions */}
        <InfoCard
          testId="card-pending"
          icon="⏳" label="Autopilot Positions" color={C.pink}
          value={apPositions.length}
          sub={apPositions.length===0?"No open autopilot trades":`${apPositions.length} Omega-managed`}
          badge={apPositions.length>0?"OPEN":undefined}
        >
          {apPositions.slice(0,3).map((p,i) => (
            <div key={i} style={{ display:"flex",justifyContent:"space-between",marginTop:6,fontSize:10,color:"rgba(255,255,255,0.55)" }}>
              <span style={{fontWeight:700}}>{p.ticker} {p.strike}{p.optionType==="call"?"C":"P"}</span>
              <span style={{color:C.gold}}>Zi {p.ziScore||"—"}</span>
            </div>
          ))}
        </InfoCard>

        {/* z$ Paper */}
        <InfoCard
          testId="card-zpaper"
          icon="📄" label="z$ Paper P&L" color={C.purple}
          value={`${sign(pnl?.ziPnl||0)}${f$(pnl?.ziPnl)}`}
          sub="Omega paper-trading ledger"
        />
      </div>

      {/* ═══════ DAILY STREAK BARS ════════════════════════════════════════════ */}
      {dayChanges.length > 1 && (
        <div style={{ marginBottom:12,padding:"14px 14px 12px",borderRadius:20,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
            <div style={{ fontSize:9,letterSpacing:1.8,color:"rgba(255,255,255,0.30)",fontWeight:800,textTransform:"uppercase" }}>Daily Streak · Last 30</div>
            <div style={{ fontSize:10,color:"rgba(255,255,255,0.35)" }}>
              {wins30}/{Math.min(dayChanges.length,30)} up · {winRate}% win
            </div>
          </div>
          <div style={{ display:"flex",alignItems:"flex-end",gap:2,height:52 }}>
            {dayChanges.slice(-30).map((b,i) => {
              const maxAbs = Math.max(...dayChanges.slice(-30).map(d=>Math.abs(d.pct)),0.01);
              const h = Math.max(3, Math.abs(b.pct)/maxAbs*48);
              const pos = b.pct>=0;
              return (
                <div key={i} title={`${b.date} · ${pos?"+":""}${b.pct.toFixed(2)}%`} style={{
                  flex:1, minWidth:0, height:`${h}px`, borderRadius:2,
                  background:`linear-gradient(180deg,${pos?C.green:C.red},${pos?"#052e16":"#450a0a"})`,
                  alignSelf:pos?"flex-end":"flex-start", opacity:0.9,
                }} />
              );
            })}
          </div>
        </div>
      )}

      {/* ═══════ OMEGA AI INSIGHT ═════════════════════════════════════════════ */}
      <div style={{ borderRadius:20, overflow:"hidden", background:"linear-gradient(145deg,rgba(167,139,250,0.07),rgba(96,165,250,0.04))", border:"1px solid rgba(167,139,250,0.18)" }}>
        <div style={{ height:2, background:"linear-gradient(90deg,#a78bfa,#60a5fa44)" }} />
        <div style={{ padding:"14px 16px 16px" }}>
          <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:10 }}>
            <div style={{ width:28,height:28,borderRadius:8,background:"linear-gradient(135deg,#a78bfa,#60a5fa)",display:"grid",placeItems:"center",fontSize:13,fontWeight:900,color:"#fff" }}>Ω</div>
            <span style={{ fontSize:9,fontWeight:900,letterSpacing:2,color:"#a78bfa",textTransform:"uppercase" }}>Omega AI Insight</span>
            <span style={{ marginLeft:"auto",width:6,height:6,borderRadius:"50%",background:"#a78bfa",animation:"pulse 2s infinite" }} />
          </div>
          <div style={{ fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.65 }}>
            {insight || <span style={{ color:"rgba(255,255,255,0.25)" }}>Analyzing portfolio…</span>}
          </div>
        </div>
      </div>

      <LifeCardPopup open={!!lifeCard} title={lifeCard?.title} accent={lifeCard?.accent||C.sky} onClose={()=>setLifeCard(null)}>
        {lifeCard?.content}
      </LifeCardPopup>
    </div>
  );
}
